/**
 * Base SVG Prompt Rules
 *
 * Shared constraints and styling rules for all AI-generated EA diagrams.
 * Ensures consistent sizing, colors, and formatting across all tabs.
 */

/** Color palette matching ea-topology.css */
const COLORS = {
    bgDark: '#0f172a',
    bgCard: '#1e293b',
    border: '#334155',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    blue: '#3b82f6',
    green: '#22c55e',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#a855f7',
    cyan: '#06b6d4',
};

/**
 * Returns the base SVG generation rules shared by all diagram prompts.
 * These rules instruct Claude on formatting, sizing, and styling.
 */
export function baseSvgRules(): string {
    return `You are generating an SVG diagram for a dark-themed enterprise architecture dashboard.

CRITICAL RULES:
1. Return ONLY raw SVG markup. No markdown fences, no explanation, no comments outside the SVG.
2. The SVG MUST start with <svg and end with </svg>.
3. Use viewBox="0 0 1920 1080" for consistent sizing.
4. Use these exact colors:
   - Background: ${COLORS.bgDark} (overall), ${COLORS.bgCard} (cards/boxes)
   - Borders: ${COLORS.border}
   - Primary text: ${COLORS.textPrimary}
   - Secondary text: ${COLORS.textSecondary}
   - Accent blue: ${COLORS.blue}
   - Accent green: ${COLORS.green}
   - Accent amber: ${COLORS.amber}
   - Accent red: ${COLORS.red}
   - Accent purple: ${COLORS.purple}
   - Accent cyan: ${COLORS.cyan}
5. Use font-family="system-ui, -apple-system, sans-serif" for all text.
6. Keep text readable: minimum 12px for labels, 14px for headings, 10px for details.
7. Include <title> elements on interactive shapes for hover tooltips.
8. Use rounded rectangles (rx="8") for cards and containers.
9. Group related elements with <g> tags and descriptive labels.
10. Do NOT include any <script> tags or event handlers.
11. Ensure the diagram is self-contained (no external references).
12. Use the ACTUAL data provided - do not invent placeholder data.
13. If there are many items, prioritize the most important ones and add a count note.`;
}

/**
 * Serializes an array of entity objects into a compact text summary for the prompt.
 * Strips metadata fields and CandidOpt arrays to reduce token usage.
 */
export function summarizeEntities<T extends Record<string, unknown>>(
    entities: T[],
    fields: string[]
): string {
    if (entities.length === 0) return '(none)';
    return entities.map(e =>
        fields.map(f => {
            const val = e[f];
            if (val === undefined || val === null) return '';
            if (Array.isArray(val) && val.length === 0) return '';
            return `${f}: ${String(val)}`;
        }).filter(Boolean).join(', ')
    ).join('\n');
}
